import { SchemaFieldTypeExtended, SchemaType } from "config/config";
import { schemaSelector } from "containers/Resources/Resources.selectors";
import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { sortAlphabetically } from "utils/sorting";
import { capitalizeFirstLetter } from "utils/utils";

// Stable identity for the default (no extra fields) case so the hook's memoised
// values are not invalidated on every render by a fresh [] literal.
const emptyAttributeFields: SchemaFieldTypeExtended[] = [];

/**
 * Custom hook that provides matching fields for filter building based on the active tab.
 * This hook processes schema fields, handles foreign key relationships, and provides
 * a filtered and sorted list of fields available for filtering.
 *
 * @param {Object} params - The hook parameters
 * @param {string} params.activeTab - The currently active tab/table name
 * @returns {Object} Object containing filtered field list and matching fields
 * @returns {Array} returns.filteredFieldList - Sorted list of fields available for filtering
 * @returns {Array} returns.matchingFields - All matching fields including extended fields
 */
export const useMatchingFields = ({
    activeTab,
    attributeFields = emptyAttributeFields,
}: {
    activeTab: string;
    /**
     * Extra synthetic filter fields to append to the field list — used to expose a
     * selected custom-object template's attribute values (`value.<attr>`) in advanced
     * search. Empty for every other caller, so behaviour is unchanged unless the
     * advanced-search filter passes them. See objectTemplateFilterFields.ts.
     */
    attributeFields?: SchemaFieldTypeExtended[];
}) => {
    // Get the schema from Redux store
    const schema: SchemaType[] = useSelector(schemaSelector);

    /**
     * Creates extended fields for fields that have foreign key relationships.
     * Extended fields combine the original field name with the referenced table and column.
     *
     * @param {SchemaType} currentField - The current schema field to process
     * @returns {Array} Array of extended fields with foreign key information
     */
    const getExtendedFields = useCallback((currentField: SchemaType) => {
        const extendedFields = currentField.fields
            .filter((field) => field.foreign_keys.length > 0)
            .map((field) => {
                // Create extended field name: "fieldName--table.column"
                const name = `${field.name}--${field.foreign_keys[0].table}.${field.foreign_keys[0].column}`;
                return { ...field, name, isExtended: true };
            });

        return extendedFields;
    }, []);

    /**
     * Recursively gets all corresponding fields including parent table fields and extended fields.
     * This function handles indirection fields by traversing up the parent table hierarchy.
     *
     * @param {SchemaType} field - The current field to process
     * @param {SchemaType[]} schema - The complete schema array
     * @param {SchemaFieldTypeExtended[]} resultFields - Accumulator for result fields (default: [])
     * @returns {SchemaFieldTypeExtended[]} Array of all corresponding fields
     */
    const getCorrespondingFields = useCallback(
        (field, schema: SchemaType[], resultFields: SchemaFieldTypeExtended[] = []) => {
            // Add extended fields (foreign key relationships)
            const extendedFields = getExtendedFields(field);
            resultFields.push(...field.fields);
            resultFields.push(...extendedFields);

            // Base case: no parent tables to process
            if (!field.parent_table_names.length) {
                return resultFields;
            } else {
                // Recursively process each parent table
                field.parent_table_names.forEach((parentTableName) => {
                    const parentField = schema.find((field) => parentTableName === field.name);
                    if (parentField) {
                        getCorrespondingFields(parentField, schema, resultFields);
                    }
                });
            }
            return resultFields;
        },
        [getExtendedFields]
    );

    /**
     * Gets all matching fields for a given active tab by finding the entry field
     * and retrieving all its corresponding fields.
     *
     * @param {SchemaType[]} schema - The complete schema array
     * @param {string} activeTab - The active tab/table name
     * @returns {Array} Array of matching fields or empty array if entry field not found
     */
    const getMatchingFields = useCallback(
        (schema: SchemaType[], activeTab) => {
            const entryField = schema.find((field) => activeTab === field.name);
            if (entryField) {
                return getCorrespondingFields(entryField, schema);
            } else {
                return [];
            }
        },
        [getCorrespondingFields]
    );

    // Memoized matching fields (from the platform schema) to avoid recalculation.
    const baseMatchingFields = useMemo(
        () => getMatchingFields(schema, activeTab) ?? [],
        [activeTab, getMatchingFields, schema]
    );

    // Append any caller-supplied synthetic attribute fields (custom-object value.<attr>
    // filters). These flow through the field dropdown, operator/value controls and the
    // emitted query exactly like platform fields, because their `name` is a plain path.
    const matchingFields = useMemo(
        () => (attributeFields.length ? [...baseMatchingFields, ...attributeFields] : baseMatchingFields),
        [baseMatchingFields, attributeFields]
    );

    /**
     * Generates a human-readable label for a field value.
     * Handles special cases like indirection fields, identity fields, and ID fields.
     *
     * @param {string} value - The field value to generate label for
     * @returns {string} Human-readable label
     */
    const getLabel = (value) => {
        const uppercase = capitalizeFirstLetter(value);

        // Handle indirection fields like 'created_by--identity.id:name'
        // Replace underscores and double dashes with spaces, skip everything after "."
        const replacedLabel = uppercase.replaceAll("_", " ").replaceAll("--", " ");
        const label = replacedLabel.split(".")[0];

        // Special case: identity fields
        if (label.match(/by identity$/)) {
            return `${label} (all attributes)`;
        }

        // Special case: Message field
        if (label === "Message") {
            return "Status message";
        }

        // Special case: before_create/on_create and similar
        if (label.match(/(on|before) (create|update|delete)$/i)) {
            return `${label} flow`;
        }

        // Special case: before_create_flow/on_create_flow and similar
        if (label.match(/(on|before) (create|update|delete) flow$/i)) {
            return `${label} (all attributes)`;
        }

        // Handle ID fields: "project id" => "project", "project id project" => "project (all attributes)"
        const idRegexp = /\sid$/;
        if (label.match(/\sid\s/)) {
            // Case: "project id project" => "project (all attributes)"
            const indexOfFirst = label.indexOf(" id ");
            const newLabel = label.substring(0, indexOfFirst);
            return `${newLabel} (all attributes)`;
        } else if (label.match(idRegexp)) {
            // Case: "project id" => "project"
            return label.replace(idRegexp, "");
        }
        return label;
    };

    // Filter and transform matching fields into selectable options
    const filteredFieldListUnsorted = useMemo(
        () =>
            matchingFields
                ?.filter((field, index, currentArray) => {
                    // NOTE: dict (JSONEncodedData) fields such as record_metadata.data
                    // are intentionally NOT excluded — the operator picker
                    // (getOperatorList case "dict" → eq/neq) and the value control
                    // (ValueField case "dict" → YAML code-editor) both handle them, and
                    // the backend filter grammar accepts an equality filter on a
                    // JSONEncodedData column (verified: record_metadata.data eq true).
                    // Excluding them blocked the "records with record_metadata
                    // integrationtest_temp=true" use-case (operator-task-fe-exists-filter-builder-fixes #3).

                    // Exclude unavailable fields
                    if (field.reading === "UNAVAILABLE") return false;

                    // Exclude specific system fields
                    if (
                        field.name === "record_type" ||
                        field.name === "activity_type" ||
                        field.name === "resource_type" ||
                        field.name.includes("organization_id") ||
                        field.name.includes("workspace_id") ||
                        field.name.includes("dependency_id") ||
                        field.name === "savepoint_retention" ||
                        field.name === "breakpoints" ||
                        field.name === "icon"
                    )
                        return false;

                    // Special filtering for execution-related tabs
                    if (activeTab.includes("execution")) {
                        if (
                            field.name === "lineno" ||
                            field.name === "break" ||
                            field.name.includes("process_id")
                        ) {
                            return false;
                        }
                    }

                    // Remove duplicate field names (keep only the first occurrence)
                    return (
                        currentArray.findIndex(
                            (currentField) => field.name === currentField.name
                        ) === index
                    );
                })
                .map((field) => {
                    return {
                        value: field.name,
                        // Synthetic attribute fields carry an explicit label (their
                        // `value.<attr>` name would be mangled by getLabel); platform
                        // fields fall back to the getLabel heuristics.
                        label: field.label ?? getLabel(field.name),
                    };
                }) || [],
        [activeTab, matchingFields]
    );

    // Sort the filtered field list alphabetically by label
    const filteredFieldList = useMemo(
        () => sortAlphabetically([...filteredFieldListUnsorted], (field) => field.label),
        [filteredFieldListUnsorted]
    );

    const defaultSchemaValues = useMemo(() => {
        // Record-creation defaults come only from the platform schema fields, never
        // from injected value.<attr> filter fields.
        return baseMatchingFields.reduce((acc, field) => {
            if (field.creating !== "UNAVAILABLE") {
                acc[field.name] = field.default;
            }
            return acc;
        }, {} as { [key: string]: any });
    }, [baseMatchingFields]);

    return {
        filteredFieldList,
        matchingFields,
        defaultSchemaValues,
    };
};
